/**
 * 侧边菜单供应商列表相关 store
 */
import { create } from 'zustand';
import { IApiSourcingContactEmail } from '@/services/swagger/easourcing/models';
import { listContactEmailsGet, unreadPost, pinToTopPut, unpinPut } from '@/services/swagger/easourcing/ContactService';
import { batchGetContactAutoReplyPost } from '@/services/swagger/easourcing/TaskReplyService';
import { toast } from '@leadsnavi/ui-kit';

interface SupplierSessionState {
  // 供应商列表数据
  supplierList: IApiSourcingContactEmail[];
  //未读数据
  unreadStatusMap: Record<string, number>;
  // 总未读数
  totalUnreadCount: number;
  // 服务端需要的分页标记参数
  cursor: string;
  loading: boolean;
  // 内部状态  避免重复请求
  _isPending: boolean;
  page: number;
  size: number;
  hasMore: boolean;
  // 虚拟滚动位置 记录在这里方便切换页面回来时恢复位置
  scrollOffset: number;
  setScrollOffset: (offset: number) => void;
  loadSupplierList: (showLoading?: boolean, overrideList?: boolean) => Promise<void>;
  reloadSupplierList: (showLoading?: boolean, clearData?: boolean) => Promise<void>;
  loadUnreadStatus: (contactIdList: string[]) => Promise<void>;
  updateUnreadStatusMap: (contactId: string, count: number) => void;
  updateSupplierItem: (contactId: string, data: Partial<IApiSourcingContactEmail>) => void;
  updateAllAutoReplyEnabled: (autoReplyEnabled: number) => void;
  /** 调用 batch 接口同步所有联系人的 autoReplyEnabled 状态 */
  syncAutoReplyStatus: () => Promise<void>;
  updateTotalUnreadCount: (count: number) => void;
  /** 传空数组调用 unreadPost，获取全部联系人未读数（含总未读），用于菜单栏初始化 */
  loadTotalUnreadCount: () => Promise<void>;
  addNewContact: (contact: IApiSourcingContactEmail) => void;
  togglePin: (contactId: string, isPinned: boolean) => Promise<void>;
}

// 创建 store
const useStore = create<SupplierSessionState>((set, get) => ({
  cursor: '',
  loading: true,
  _isPending: false,
  hasMore: true,
  totalUnreadCount: 0,
  page: 1,
  size: 100,
  supplierList: [],
  unreadStatusMap: {},
  scrollOffset: 0,
  setScrollOffset: (offset: number) => set({ scrollOffset: offset }),
  loadSupplierList: async (showLoading?: boolean, overrideList?: boolean) => {
    try {
      const { loadUnreadStatus, page, size, cursor = '', supplierList = [], _isPending, hasMore } = get();
      if (_isPending || !hasMore) {
        return;
      }

      if (showLoading) {
        set({ loading: true });
      }

      set({ _isPending: true });
      const res = await listContactEmailsGet({ cursor, size });
      const newList = !overrideList ? supplierList.concat(res?.data || []) : res?.data || [];

      set({
        supplierList: newList,
        cursor: res?.cursor || '',
        page: page + 1,
        size,
        hasMore: res?.data.length >= size,
      });
      const contactIdList = res?.data?.map(item => item.id) || [];
      if (contactIdList.length > 0) {
        // 异步获取未读状态Map 无需等待
        loadUnreadStatus(contactIdList);
      }
      set({ _isPending: false });
    } catch {
      set({ _isPending: false });
    } finally {
      set({ loading: false });
    }
  },
  reloadSupplierList: async (showLoading?: boolean, clearData?: boolean) => {
    try {
      const { loadSupplierList, _isPending } = get();
      if (_isPending) {
        return;
      }

      if (clearData) {
        set({ supplierList: [], unreadStatusMap: {} });
      }

      set({
        cursor: '',
        scrollOffset: 0,
        hasMore: true,
        page: 1,
      });
      await loadSupplierList(showLoading, true);
    } catch {
      console.error('Failed to reload supplier list');
    }
  },
  loadUnreadStatus: async (contactIdList: string[]) => {
    try {
      const res = await unreadPost({ contactIdList });
      const unreadStatusMap: Record<string, number> = {};
      res.forEach(item => {
        unreadStatusMap[item.contactId] = item.unreadCount || 0;
      });
      set({
        unreadStatusMap: {
          ...get().unreadStatusMap,
          ...unreadStatusMap,
        },
        totalUnreadCount: unreadStatusMap['-1'] || 0,
      });
    } catch {
      console.error('Failed to load unread status');
    }
  },
  updateUnreadStatusMap: (contactId: string, count: number) => {
    set(state => ({
      unreadStatusMap: {
        ...state.unreadStatusMap,
        [contactId]: count,
      },
    }));
  },
  updateSupplierItem: (contactId: string, data: Partial<IApiSourcingContactEmail>) => {
    set(state => ({
      supplierList: state.supplierList.map(item => (item.id === contactId ? { ...item, ...data } : item)),
    }));
  },
  updateAllAutoReplyEnabled: (autoReplyEnabled: number) => {
    set(state => ({
      supplierList: state.supplierList.map(item => ({ ...item, autoReplyEnabled })),
    }));
  },
  syncAutoReplyStatus: async () => {
    const { supplierList } = get();
    const contactIds = supplierList.map(item => item.id);
    if (contactIds.length === 0) return;
    try {
      const res = await batchGetContactAutoReplyPost({ contactIds });
      const autoReplyMap: Record<string, number> = {};
      (res?.data ?? []).forEach(item => {
        autoReplyMap[String(item.contactId)] = item.autoReplyEnabled;
      });
      set(state => ({
        supplierList: state.supplierList.map(item =>
          String(item.id) in autoReplyMap ? { ...item, autoReplyEnabled: autoReplyMap[String(item.id)] } : item
        ),
      }));
    } catch {
      console.error('Failed to sync auto reply status');
    }
  },
  updateTotalUnreadCount: (count: number) => {
    set({ totalUnreadCount: count });
  },
  loadTotalUnreadCount: async () => {
    try {
      const res = await unreadPost({ contactIdList: [] });
      const totalItem = res.find(item => item.contactId === '-1');
      if (totalItem != null) {
        set({ totalUnreadCount: totalItem.unreadCount || 0 });
      }
    } catch {
      console.error('Failed to load total unread count');
    }
  },
  togglePin: async (contactId: string, isPinned: boolean) => {
    try {
      if (isPinned) {
        await pinToTopPut({ id: contactId });
      } else {
        await unpinPut({ id: contactId });
      }
      // 接口成功后重新拉取列表，以同步服务端最新排序
      await get().reloadSupplierList();
    } catch {
      toast.error(isPinned ? 'Failed to pin contact' : 'Failed to unpin contact');
    }
  },
  addNewContact: (contact: IApiSourcingContactEmail) => {
    set(state => {
      const list = state.supplierList;
      if (list.find(item => item.id === contact.id)) {
        // 已存在则不添加
        return {};
      }
      // 第一个非置顶元素的下标，即置顶区域的结束位置
      const firstNormalIndex = list.findIndex(item => item.sortType !== 'PINNED_TOP');
      // 非置顶联系人：插入到置顶区域之后的最前面
      if (firstNormalIndex === -1) {
        // 全部都是置顶，追加到末尾
        return { supplierList: [...list, contact] };
      }
      return {
        supplierList: [...list.slice(0, firstNormalIndex), contact, ...list.slice(firstNormalIndex)],
      };
    });
  },
}));

export default useStore;
